import { Button, DrawerFooter, Text, useToast, VStack } from "@chakra-ui/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import AsyncSelect from "react-select/async";
import {
  isValidEmailFunc,
  isValidName,
  isValidPhoneFunc,
} from "../../../utils";
import {
  fetchRoles,
  fetchUserById,
  loadDealershipsApi,
  updateUser,
} from "../../../utils/api/users.api";
import { useUserInfo } from "../../../utils/hooks/useUserInfo";
import { CustomRadio, Drawer, Input, PhoneInput } from "../../common";
import Loader from "../../common/Spinner";
import SwitchToggle from "../../common/Switch";
import DebouncedAsyncSelect from "../../common/DebouncedAsyncSelect";

type data = {
  isSimpSocial?: boolean;
  isOpen: boolean;
  onClose: any;
  refetchParent: any;
  id: number;
  isFromDealershipMenu?: boolean;
};
type permission = {
  allowed: boolean;
  display_name: string;
  module_key: string;
};

interface ApiResult {
  data: any;
}
interface ApiRequest {
  user: {
    first_name: string;
    last_name: string;
    phone: string;
    dealership_ids: number[];
    email: string;
    user_role_attributes: {
      role_id: number;
      permissions: permission[];
    };
  };
}
const EditUsers = (props: data) => {
  const {
    isSimpSocial,
    isOpen,
    onClose,
    refetchParent,
    id,
    isFromDealershipMenu,
  } = props;
  const toast = useToast();
  const userrole = useUserInfo("role");
  //..........api calling..............
  const { data, isLoading } = useQuery({
    queryKey: ["roles"],
    queryFn: () =>
      isSimpSocial
        ? fetchRoles({
            simp_social_user: isSimpSocial,
          })
        : fetchRoles(),
  });

  const { data: user, isLoading: editloading } = useQuery({
    queryKey: ["userbyid"],
    queryFn: () => fetchUserById(id),
    refetchOnWindowFocus: false,
    retry: false,
  });

  const [name, setName] = useState("");
  const [lastname, setLastname] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState("");
  const [dealerships, setDealerships] = useState<SelectOptionProps[]>([]);
  const [roleOptions, setRoleOptions] = useState<SelectOptionProps[]>([]);
  const [permissionOptions, setPermissionOptions] = useState<permission[]>([]);

  //.......error states......
  const [nameError, setNameError] = useState("");
  const [lastnameError, setLastnameError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [emailError, setEmailError] = useState("");

  const [dealershipError, setDealershipError] = useState("");
  const isValidDealership = () => {
    if (isSimpSocial || isFromDealershipMenu) {
      setDealershipError("");
      return true;
    }

    if (!dealerships.some((dealership) => dealership.value)) {
      setDealershipError("Please select at least one dealership.");
      return false;
    }

    setDealershipError("");
    return true;
  };
  useEffect(() => {
    setName(user?.first_name);
    setLastname(user?.last_name);
    setEmail(user?.email);
    setPhone(user?.phone);

    setRoleOptions(
      data?.roles?.map((role: any) => ({
        value: role.id.toString(),
        label: role.title,
      }))
    );

    setRole(
      data?.roles
        ?.find((r: any) => r.title.toString() === user?.access_level)
        ?.id.toString()
    );

    if (Array.isArray(user?.permissions)) {
      setPermissionOptions(user?.permissions);
    }

    setDealerships(user?.dealerships);
  }, [data, user]);

  const mutation = useMutation<ApiResult, Error, ApiRequest, unknown>({
    mutationFn: async (payload) => {
      try {
        const response = await updateUser(id, payload, {
          menu: "User",
          "Sub-Menu": "",
        });
        return { data: response.data };
      } catch (error) {
        throw error;
      }
    },
    onSuccess: () => {
      refetchParent();
      onClose();
      toast({
        description: "User edited successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    },
    onError: (error: any) => {
      toast({
        description: `Error editing user: ${
          error?.response?.data?.error?.toString() ?? "Something went wrong"
        }`,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    },
  });

  const handleSubmit = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    let fnameError = isValidName(name, setNameError, "First Name");
    let lnameError = isValidName(lastname, setLastnameError, "Last Name");
    let phoneError = isValidPhoneFunc(phone, setPhoneError);
    let emailError = isValidEmailFunc(email, setEmailError);
    let dealershipError = isValidDealership();

    if (
      !fnameError ||
      !lnameError ||
      !phoneError ||
      !emailError ||
      !dealershipError
    ) {
      return;
    }

    const requestData: ApiRequest = {
      user: {
        first_name: name,
        last_name: lastname,
        phone: phone ?? "",
        email: email ?? "",
        dealership_ids: dealerships?.map((d) => Number(d.value)),
        user_role_attributes: {
          role_id: Number(role),
          permissions: permissionOptions || [],
        },
      },
    };

    mutation.mutate(requestData);
  };

  const handlePermissionToggle = (moduleKey: string, isChecked: boolean) => {
    setPermissionOptions((prevPermissions: permission[]) => {
      return prevPermissions?.map((permission: permission) =>
        permission.module_key === moduleKey
          ? { ...permission, allowed: isChecked }
          : permission
      );
    });
  };

  const loadDealerships = async (inputValue: string) => {
    try {
      const response = await loadDealershipsApi({
        status: "active",
        searchValue: inputValue,
      });
      const options = response?.dealerships?.map((dealership: any) => ({
        value: dealership.id,
        label: dealership.name,
      }));
      return options;
    } catch (error) {
      console.error("Error fetching dealership data:", error);
      return [];
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Edit User"
      isLoading={isLoading || mutation?.isLoading || editloading}
    >
      <VStack alignItems="flex-start" padding="1rem 1.5rem" w="100%" gap="1rem">
        <Input
          placeholder="Enter First Name"
          label="First Name"
          onChange={(e: any) => {
            setNameError("");
            setName(e.target.value);
          }}
          value={name}
          isRequired={true}
          error={nameError}
        />
        <Input
          placeholder="Enter Last Name"
          label="Last Name"
          onChange={(e: any) => {
            setLastnameError("");
            setLastname(e.target.value);
          }}
          value={lastname}
          isRequired={true}
          error={lastnameError}
        />

        <Input
          placeholder="Enter email address"
          label="Email"
          value={email}
          isRequired={true}
          isDisabled={userrole !== "SimpSocial Owner"}
          error={emailError}
          onChange={(e: any) => {
            setEmailError("");
            setEmail(e.target.value);
          }}
        />

        <PhoneInput
          label="Phone Number"
          placeholder="Enter phone number"
          onChange={(value) => {
            setPhoneError("");
            setPhone(value);
          }}
          isRequired={false}
          value={phone}
          error={phoneError}
        />

        {!isSimpSocial && !isFromDealershipMenu && (
          <>
            <Text
              textStyle="h4"
              fontWeight="600"
              fontSize="0.875rem"
              color="var(--grey-900)"
            >
              Assign Dealerships
            </Text>
            <DebouncedAsyncSelect
              loadOptions={(inputValue: string) => loadDealerships(inputValue)}
              onChange={(value: any) => setDealerships(value)}
              placeholder="Enter the dealerships"
              isMulti={true}
              value={dealerships}
              styles={{
                control: (provided: any) => ({
                  ...provided,
                  minWidth: "442px",
                  padding: "6px 7px",
                  borderRadius: "0.5rem",
                  border: `1px solid var(--grey-300)`,
                  marginTop: "-10px",
                }),
              }}
              defaultOptions
            />
            <Text textStyle="captionSmall" color="var(--red-600)">
              {dealershipError}
            </Text>
          </>
        )}

        <CustomRadio
          label="Place choose an access level for the user"
          direction="column"
          options={roleOptions}
          value={role}
          setValue={(value: string) => {
            setRole(value);
            const selectedRole = data?.roles.find(
              (r: any) => r.id.toString() === value
            );
            setPermissionOptions(
              selectedRole?.permissions?.map((permission: permission) => ({
                allowed: permission.allowed,
                display_name: permission.display_name,
                module_key: permission.module_key,
              }))
            );
          }}
        />

        {permissionOptions?.length && (
          <VStack
            padding="1rem"
            background="var(--grey-100)"
            border="1px solid var(--grey-300)"
            borderRadius="0.5rem"
            gap="1rem"
            w="100%"
            alignItems="flex-start"
          >
            <Text textStyle="h6" color="var(--grey-800)">
              Set custom permissions below
            </Text>

            {permissionOptions?.map((permission: any) => (
              <SwitchToggle
                key={permission.module_key}
                defaultChecked={permission.allowed}
                label={permission.display_name}
                onChange={(isChecked) =>
                  handlePermissionToggle(permission.module_key, isChecked)
                }
              />
            ))}
          </VStack>
        )}
      </VStack>
      <DrawerFooter
        position="absolute"
        bottom="0"
        left="0"
        width="100%"
        borderTop="1px solid var(--grey-300)"
        display="flex"
        alignItems="center"
        gap="0.81rem"
      >
        <Button variant="outline" w="100%" size="xl" onClick={onClose}>
          Cancel
        </Button>
        <Button w="100%" size="xl" onClick={handleSubmit}>
          Edit User
        </Button>
      </DrawerFooter>

      {mutation.isLoading && <Loader />}
    </Drawer>
  );
};

export default EditUsers;
